import Avatar from '@mui/material/Avatar';
import type { PopoverProps } from '@mui/material/Popover';
import { Box, Divider, styled, Typography, typographyClasses } from '@mui/material';
import React from 'react';
import type { useUserRoles } from 'ogp/components/views/sidebar/use-user-roles';
import { UserRoles } from './user-roles';
import { FeatureFlagsList } from './feature-flags-list';

type Props = {
  userLabel: string;
  userRoles: ReturnType<typeof useUserRoles>;
  id: string | undefined;
};

const UserPopoverOgp = ({
  userLabel,
  userRoles,
  id,
  children,
  ...props
}: React.PropsWithChildren<Props & PopoverProps>) => {
  if (!userRoles) {
    return null;
  }

  const { companyRoles } = userRoles;

  return (
    <StyledPopover id={id} {...props}>
      <StyledUserBox>
        <StyledAvatar alt={userLabel}>
          <StyledAvatarLabel variant="h2">{userLabel[0]}</StyledAvatarLabel>
        </StyledAvatar>
        <Box>
          <StyledUserLabel>{userLabel}</StyledUserLabel>
          <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
            {companyRoles.map((role) => (
              <StyledRole variant="subtitle1" key={role}>
                {role}
              </StyledRole>
            ))}
          </Box>
        </Box>
      </StyledUserBox>
      <Box mt={2} mb={2}>
        <Divider />
      </Box>
      <Box>
        <FeatureFlagsList />
      </Box>
      <Box>
        <UserRoles roles={userRoles} />
      </Box>
      {children}
    </StyledPopover>
  );
};

const usePopoverHandlers = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const id = anchorEl ? 'simple-popover' : undefined;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return { id, anchorEl, handleOpen, handleClose };
};

type PropsHq = {
  userLabel: string;
  userRoles: ReturnType<typeof useUserRoles>;
};

const UserPopoverHq = ({ userLabel, userRoles }: PropsHq) => {
  if (!userRoles) {
    return null;
  }

  const { companyRoles } = userRoles;

  return (
    <Box>
      <StyledUserBox>
        <StyledAvatarHq alt={userLabel}>
          <StyledAvatarLabelHq variant="h2">{userLabel[0]}</StyledAvatarLabelHq>
        </StyledAvatarHq>
        <Box>
          <StyledUserLabelHq>{userLabel}</StyledUserLabelHq>
          <Box style={{ display: 'flex', flexWrap: 'wrap' }}>
            {companyRoles.map((role) => (
              <StyledRole variant="subtitle1" key={role}>
                {role}
              </StyledRole>
            ))}
          </Box>
        </Box>
      </StyledUserBox>
      <Box width="100%">
        <FeatureFlagsList />
      </Box>
      <Box width="100%" ml={1} mr={1}>
        <UserRoles roles={userRoles} />
      </Box>
    </Box>
  );
};

const StyledPopover = styled(Box)(({ theme }) => ({
  width: '398px',
  overflow: 'auto',
  flex: 1,
  '&::-webkit-scrollbar': {
    width: '22px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.lwBlack[20],
    backgroundClip: 'content-box',
    border: '8px solid transparent',
    borderRadius: '12px',
    '&:hover': {
      background: theme.palette.lwBlack[40],
      backgroundClip: 'content-box',
      border: '8px solid transparent',
      borderRadius: '12px',
    },
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.lwBlack[10],
    border: '8px solid white',
    borderRadius: '12px',
  },
}));

const StyledUserBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(20),
  width: theme.spacing(20),
  marginRight: theme.spacing(4),
}));

const StyledAvatarLabel = styled(Typography)(() => ({
  textTransform: 'capitalize',
  fontWeight: 500,

  [`&.${typographyClasses.root}`]: {
    fontSize: '34px',
  },
}));

const StyledUserLabel = styled(Typography)(({ theme }) => ({
  lineHeight: theme.spacing(8),
}));

const StyledRole = styled(Typography)(({ theme }) => ({
  marginRight: theme.spacing(2),
  border: `1px solid ${theme.palette.lwBlack[60]}`,
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  borderRadius: theme.spacing(6),
  marginBottom: theme.spacing(2),
}));

const StyledAvatarHq = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(10),
  width: theme.spacing(10),
  marginRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledAvatarLabelHq = styled(Typography)(() => ({
  textTransform: 'capitalize',
  fontWeight: 500,
}));

const StyledUserLabelHq = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(2),
}));

export { UserPopoverOgp, UserPopoverHq, usePopoverHandlers };
